import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {StateControllerService} from '../../api';

export type ChatThreadMode = 'CLASSIC' | 'MYCHECK' | 'EDIT';
export type MyCheckType = 'KI' | 'IDS' | 'HYBRID';

export interface ChatThreadDto {
  id: string;
  projectId?: number;
  topic: string;
  mode: ChatThreadMode;
  rulesetId?: string;
  mycheckType?: MyCheckType;
  messageCount: number;
  createdAt: string;
  lastMessageAt?: string;
  /** Stand einer myCheck-Serie im Quickstart (BIC-Backend#189); fehlt ohne Serie. */
  mycheckSeries?: ChatMycheckSeriesDto | null;
}

/** Stand eines Punkts einer myCheck-Serie. */
export type ChatMycheckSeriesStatus = 'PLANNED' | 'CREATED' | 'COMPLETE' | 'INCOMPLETE' | 'SKIPPED';

export interface ChatMycheckSeriesItemDto {
  position: number;
  name: string;
  status: ChatMycheckSeriesStatus;
  rulesetId?: string | null;
  mycheckType?: MyCheckType | null;
  /** Offene Punkte eines Entwurfs bzw. Grund des Überspringens – JSON-Liste von `{field?, message}`. */
  issuesJson?: string | null;
}

/**
 * Mehrere myChecks in einem Quickstart (Signal `mycheckSeries` und `ChatThreadDto.mycheckSeries`):
 * Plan, Stand je Punkt, nächster Schritt und der Prüflauf am Ende.
 */
export interface ChatMycheckSeriesDto {
  items: ChatMycheckSeriesItemDto[];
  confirmed: boolean;
  next?: { position: number; name: string } | null;
  finished: boolean;
  checkRunDue: boolean;
  checkRunDefinitionId?: string | null;
}

/**
 * Stand des chatBIC-Kontingents, auf das Chats in einem Projekt zählen (`GET chat/quota`).
 *
 * <p>`scope` sagt, wessen Abo es ist: in einem Organisationsprojekt das der Organisation, sonst das
 * des Projektbesitzers – derselbe Nutzer kann also im einen Projekt gesperrt sein und im anderen
 * nicht.
 */
export interface ChatQuotaDto {
  used: number;
  limit?: number | null;
  exceeded: boolean;
  resetsAt?: string | null;
  scope?: 'ORGANISATION' | 'PERSONAL' | null;
}

export interface ChatMessageDto {
  id: string;
  seq: number;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM_EVENT';
  content?: string;
  uiBlockJson?: string;
  sourcesJson?: string;
  contextJson?: string;
  status: 'STREAMING' | 'COMPLETE' | 'ABORTED' | 'FAILED' | 'SUPERSEDED';
  createdAt: string;
  /** Daumen an der Antwort: 1 hilfreich, -1 nicht hilfreich. */
  feedbackRating?: number | null;
}

/**
 * Die Chatverwaltung im BIC-Backend.
 *
 * <p>Von Hand geschrieben und nicht generiert: der Generator braucht ein laufendes Backend
 * (`./api-client-generation.sh`), und generierte Ordner werden nicht eingecheckt. Sobald der
 * Client neu erzeugt wurde, kann dieser Service durch `ChatControllerService` ersetzt werden –
 * die Signaturen sind bewusst deckungsgleich gehalten.
 *
 * <p>Zur Aufteilung der Endpunkte: projektgebundene Aufrufe tragen `projectId` als Query-Parameter,
 * weil die Rechteprüfung im Backend genau dort danach sucht. Projektlose Verläufe (Wissensfragen
 * ohne Projektbezug) haben eigene `/global`-Pfade.
 */
@Injectable({providedIn: 'root'})
export class ChatApiService {
  private http = inject(HttpClient);
  private stateControllerService = inject(StateControllerService);

  private get base(): string {
    return `${(this.stateControllerService as any).basePath ?? ''}/chat`;
  }

  createThread(projectId: number | undefined, body: {
    mode: ChatThreadMode;
    rulesetId?: string;
    mycheckType?: MyCheckType;
    topic?: string;
  }): Promise<ChatThreadDto> {
    const url = projectId != null
      ? `${this.base}/threads?projectId=${projectId}`
      : `${this.base}/threads/global`;
    return firstValueFrom(this.http.post<ChatThreadDto>(url, body));
  }

  quota(projectId: number): Promise<ChatQuotaDto> {
    return firstValueFrom(this.http.get<ChatQuotaDto>(`${this.base}/quota?projectId=${projectId}`));
  }

  listThreads(projectId: number | undefined, mode: ChatThreadMode, page = 0, size = 20): Promise<ChatThreadDto[]> {
    const url = projectId != null
      ? `${this.base}/threads?projectId=${projectId}&mode=${mode}&page=${page}&size=${size}`
      : `${this.base}/threads/global?mode=${mode}&page=${page}&size=${size}`;
    return firstValueFrom(this.http.get<ChatThreadDto[]>(url));
  }

  /** Der Verlauf eines myChecks – ersetzt die frühere Suche über alle Threads. */
  threadByRuleset(rulesetId: string): Promise<ChatThreadDto | null> {
    return firstValueFrom(this.http.get<ChatThreadDto>(`${this.base}/threads/by-ruleset/${rulesetId}`))
      .catch(() => null);
  }

  messages(threadId: string, before?: number, size = 50): Promise<ChatMessageDto[]> {
    const query = before != null ? `?before=${before}&size=${size}` : `?size=${size}`;
    return firstValueFrom(this.http.get<ChatMessageDto[]>(`${this.base}/threads/${threadId}/messages${query}`));
  }

  rename(threadId: string, topic: string): Promise<ChatThreadDto> {
    return firstValueFrom(this.http.patch<ChatThreadDto>(`${this.base}/threads/${threadId}`, {topic}));
  }

  deleteThread(threadId: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.base}/threads/${threadId}`));
  }

  deleteAll(projectId: number): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.base}/threads?projectId=${projectId}`));
  }

  /** Ereignisse eines Turns ab einem Cursor – nach einem Verbindungsabbruch. */
  /** Stopp-Knopf. 204 auch, wenn der Turn schon fertig ist – dann gibt es nichts mehr zu stoppen. */
  cancelTurn(turnId: string): Promise<void> {
    return firstValueFrom(this.http.post<void>(`${this.base}/turns/${turnId}/cancel`, {}));
  }

  /** Daumen an einer Antwort; `rating: null` nimmt die Bewertung zurück. */
  setFeedback(threadId: string, messageId: string, rating: 1 | -1 | null, comment?: string): Promise<ChatMessageDto> {
    return firstValueFrom(this.http.put<ChatMessageDto>(
      `${this.base}/threads/${threadId}/messages/${messageId}/feedback`, {rating, comment: comment ?? null}));
  }

  replay(turnId: string, fromSeq = 0): Promise<any[]> {
    return firstValueFrom(this.http.get<any[]>(`${this.base}/turns/${turnId}/stream?fromSeq=${fromSeq}`));
  }
}
